/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { ListItemButtonProps, SxProps, Theme } from '@mui/material';
import {
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';
import { pluginsActions } from '@opengeoweb/plugins';
import type { ConfigType } from '@opengeoweb/shared';
import { uiActions, uiTypes } from '@opengeoweb/store';
import { ChevronDown, ChevronUp, ExitDomain } from '@opengeoweb/theme';
import type { WorkspaceModuleStore } from '@opengeoweb/workspace';
import {
  createBalancedMosaicNode,
  workspaceActions,
  selectors as workspaceSelectors,
} from '@opengeoweb/workspace';

const styles = {
  paper: {
    '& .MuiDrawer-paper': {
      top: '40px',
    },
  },
  drawer: {
    width: '250px',
    height: 'calc(100% -  40px)',
    overflowY: 'scroll',
  },
  nested: (theme: Theme): SxProps<Theme> => ({
    paddingLeft: theme.spacing(4),
  }),
  listItemPrimary: {
    '& .MuiListItemText-primary': {
      fontWeight: '500',
    },
  },
  listItemExternal: {
    paddingLeft: 0.5,
    color: 'geowebColors.typographyAndIcons.textLinkActive',
  },
};
export const PUBLIC_WARNINGS = 'Warnings';
export const TRAJKS = 'Trajks';

export const publicWarningDialogType = uiTypes.DialogTypes.PublicWarnings;

const LinkedListItemButton = ({
  workspaceId,
  title,
  handleClick,
  ...props
}: {
  workspaceId: string;
  title: string;
  handleClick: (id: string) => (event: React.MouseEvent) => void;
} & ListItemButtonProps): React.ReactElement => (
  <ListItem disablePadding>
    <ListItemButton
      component={Link}
      onClick={handleClick(workspaceId)}
      to={`/workspace/${workspaceId}`}
      {...props}
    >
      <ListItemText primary={title} sx={styles.listItemPrimary} />
    </ListItemButton>
  </ListItem>
);

interface ConfigAppMenuLink {
  title: string;
  url: string;
}

const parseExternalApplications = (
  configExternalAppMenuLinks?: string,
): ConfigAppMenuLink[] => {
  if (!configExternalAppMenuLinks) {
    return [];
  }

  try {
    const parsedExternalAppMenuLinks = JSON.parse(
      // The CI/CD environment and config.json expect strings for its keys, therefore we need to
      // do a trick by encoding/decoding spaces and double quotes to end up with a valid json
      configExternalAppMenuLinks.replaceAll('%20', ' ').replaceAll("'", '"'),
    );
    if (Array.isArray(parsedExternalAppMenuLinks)) {
      return parsedExternalAppMenuLinks as ConfigAppMenuLink[];
    }
    return [];
  } catch (error) {
    console.error(
      'Failed to parse GW_EXTERNAL_APPLICATIONS_MENU_LINKS:',
      error,
    );
    return [];
  }
};

export interface MenuProps {
  config?: ConfigType;
}

const Menu: React.FC<MenuProps> = ({ config }: MenuProps) => {
  const dispatch = useDispatch();

  const mosaicNode = useSelector((store: WorkspaceModuleStore) =>
    workspaceSelectors.getMosaicNode(store),
  );

  const autoArrange = (): void => {
    dispatch(
      workspaceActions.updateWorkspaceViews({
        mosaicNode: createBalancedMosaicNode(mosaicNode),
      }),
    );
  };

  const openSyncgroupsDialog = React.useCallback(() => {
    dispatch(
      uiActions.setToggleOpenDialog({
        type: uiTypes.DialogTypes.SyncGroups,
        setOpen: true,
      }),
    );
  }, [dispatch]);

  const fetchWorkspace = React.useCallback(
    (workspaceId: string) => {
      dispatch(workspaceActions.fetchWorkspace({ workspaceId }));
    },
    [dispatch],
  );

  const WARNINGS_WORKSPACE_ID =
    config?.GW_WARNINGS_WORKSPACE_ID ?? 'screenConfigWarnings';
  const SPACE_WEATHER_WORKSPACE_ID =
    config?.GW_SPACE_WEATHER_WORKSPACE_ID ?? 'spaceweather';
  const TRAJKS_WORKSPACE_ID = config?.GW_TRAJKS_WORKSPACE_ID;
  const NAF_WORKSPACE_ID = config?.GW_NAF_WORKSPACE_ID;

  const showTaf = config?.GW_TAF_BASE_URL;
  const showNaf = NAF_WORKSPACE_ID && showTaf;
  const showSigmet = config?.GW_HIDE_SIGMET_MENU
    ? false
    : config?.GW_SIGMET_BASE_URL;
  const showAirmet = config?.GW_HIDE_AIRMET_MENU
    ? false
    : config?.GW_AIRMET_BASE_URL;

  const [openAviation, setOpenAviation] = React.useState(true);

  const aviationProducts = [
    showAirmet && { id: 'screenConfigAirmet', title: 'AIRMET' },
    showSigmet && { id: 'screenConfigSigmet', title: 'SIGMET' },
    showTaf && { id: 'screenConfigTaf', title: 'TAF' },
    showNaf && { id: NAF_WORKSPACE_ID!, title: 'NAF' },
  ].filter(Boolean) as { id: string; title: string }[];

  const showAviationMenu = aviationProducts.length > 0;

  const externalApplications = parseExternalApplications(
    config?.GW_EXTERNAL_APPLICATIONS_MENU_LINKS,
  );

  const hasPluginsConfigured =
    config?.GW_PLUGIN_BACKEND_URL || config?.GW_PLUGIN_TEST_URL;
  const isDebugEnabled = config?.GW_PLUGIN_DEBUG ?? false;

  const isKeyExternalPressed = React.useRef(false);

  const handleWorkspaceClick =
    (id: string) =>
    (event: React.MouseEvent): void => {
      if (isKeyExternalPressed.current) {
        event.stopPropagation();
        return;
      }
      fetchWorkspace(id);
    };

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Meta' || event.key === 'Control') {
        isKeyExternalPressed.current = true;
      }
    };

    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.key === 'Meta' || event.key === 'Control') {
        isKeyExternalPressed.current = false;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return (): void => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  return (
    <List data-testid="drawer">
      {/* Auto arrange windows */}
      <ListItem disablePadding>
        <ListItemButton
          onClick={(): void => {
            autoArrange();
          }}
        >
          <ListItemText
            primary="Auto Arrange Windows"
            sx={styles.listItemPrimary}
          />
        </ListItemButton>
      </ListItem>
      {/* Sync Groups */}
      <ListItem disablePadding>
        <ListItemButton
          data-testid="syncGroupsButton"
          divider
          onClick={(): void => {
            openSyncgroupsDialog();
          }}
        >
          <ListItemText primary="Sync Groups" sx={styles.listItemPrimary} />
        </ListItemButton>
      </ListItem>

      {showAviationMenu ? (
        <>
          <ListItem disablePadding>
            <ListItemButton
              onClick={(event: React.MouseEvent): void => {
                event.stopPropagation();
                setOpenAviation(!openAviation);
              }}
            >
              <ListItemText primary="Aviation" sx={styles.listItemPrimary} />
              {openAviation ? <ChevronUp /> : <ChevronDown />}
            </ListItemButton>
          </ListItem>
          <Collapse
            component="li"
            in={openAviation}
            timeout="auto"
            unmountOnExit
          >
            <List disablePadding>
              {aviationProducts.map((aviationProduct) => (
                <LinkedListItemButton
                  dense
                  handleClick={handleWorkspaceClick}
                  key={aviationProduct.id}
                  sx={styles.nested as SxProps<Theme>}
                  title={aviationProduct.title}
                  workspaceId={aviationProduct.id}
                />
              ))}
            </List>
          </Collapse>
        </>
      ) : null}

      {/* Space Weather */}
      {config?.GW_FEATURE_MODULE_SPACE_WEATHER ? (
        <LinkedListItemButton
          handleClick={handleWorkspaceClick}
          title="Space Weather Forecast"
          workspaceId={SPACE_WEATHER_WORKSPACE_ID}
        />
      ) : null}

      {/** TRAJKS */}
      {TRAJKS_WORKSPACE_ID ? (
        <LinkedListItemButton
          handleClick={handleWorkspaceClick}
          title={TRAJKS}
          workspaceId={TRAJKS_WORKSPACE_ID}
        />
      ) : null}

      {/** Warnings */}
      {config?.GW_DRAWINGS_BASE_URL !== undefined &&
        config?.GW_PRESET_BACKEND_URL !== undefined && (
          <LinkedListItemButton
            handleClick={handleWorkspaceClick}
            title={PUBLIC_WARNINGS}
            workspaceId={WARNINGS_WORKSPACE_ID}
          />
        )}
      {/* External Applications */}
      {externalApplications.length > 0 && (
        <>
          <Divider component="li" />
          {externalApplications.map((configAppMenuLink) => (
            <ListItem disablePadding key={configAppMenuLink.title}>
              <ListItemButton
                component="a"
                href={configAppMenuLink.url}
                rel="noopener noreferrer"
                target="_blank"
              >
                <ExitDomain sx={styles.listItemExternal} />
                <ListItemText
                  primary={configAppMenuLink.title}
                  sx={styles.listItemExternal}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </>
      )}
      {!!(hasPluginsConfigured && isDebugEnabled) && (
        <ListItem disablePadding>
          <ListItemButton
            onClick={(): void => {
              dispatch(pluginsActions.toggleDebugDialog({ shouldOpen: true }));
            }}
          >
            <ListItemText primary="Plugin Debug" sx={styles.listItemPrimary} />
          </ListItemButton>
        </ListItem>
      )}
    </List>
  );
};

export default Menu;
